import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Pencil, FlaskConical } from "lucide-react";
import { call } from "./common";

const HEALTH = { ok: "bg-emerald-100 text-emerald-700", chain: "bg-amber-100 text-amber-800", loop: "bg-red-100 text-red-700",
  dead_target: "bg-red-100 text-red-700", external: "bg-red-100 text-red-700", shadows_live_page: "bg-amber-100 text-amber-800" };

export default function RedirectRules({ tick, onChange, errorsOnly, onTest, onEdit }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [errs, setErrs] = useState(errorsOnly);
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/url-repair/redirects", { params: { q, skip, limit: 50, health: errs ? "errors" : "" } }).then((r) => setData(r.data)), 250);
    return () => clearTimeout(t);
  }, [q, skip, errs, tick]);
  const toggle = (r) => call(() => api.put(`/admin/url-repair/redirects/${r.id}`, { active: !(r.active !== false) }), "Updated").then((x) => x && onChange());
  const del = (r) => window.confirm(`Delete redirect ${r.from_path}? (You can restore it from the audit log.)`) &&
    call(() => api.delete(`/admin/url-repair/redirects/${r.id}`), "Deleted").then((x) => x && onChange());
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <Input value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }} placeholder="Search from / to path" className="w-64 h-9" data-testid="ur-rules-search" />
        <label className="text-sm flex items-center gap-1.5"><input type="checkbox" checked={errs} onChange={(e) => { setErrs(e.target.checked); setSkip(0); }} data-testid="ur-rules-errors" />Errors only</label>
        {data && <span className="text-sm text-muted-foreground" data-testid="ur-rules-total">{data.total} rules</span>}
      </div>
      <div className="rounded-xl border bg-white divide-y">
        {!data ? <p className="p-3 text-sm text-muted-foreground">Loading…</p> : data.items.length === 0 ? <p className="p-3 text-sm text-muted-foreground">No redirect rules.</p>
          : data.items.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 p-3 text-sm" data-testid={`ur-rule-${r.id}`}>
              <div className="min-w-0 flex-1 font-mono text-xs break-all">
                <span className={r.active === false ? "line-through text-muted-foreground" : "text-steel"}>{r.from_path}</span>
                <span className="text-muted-foreground"> → </span><span className="text-emerald-700">{r.to_path}</span>
                <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px]">{r.code}</span>
                <span className="ml-1 text-[10px] text-muted-foreground">{r.source || "manual"}</span>
              </div>
              <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${HEALTH[r.health || "ok"] || ""}`}>{(r.health || "ok").replace("_", " ")}</span>
              <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={r.active !== false} onChange={() => toggle(r)} data-testid={`ur-rule-active-${r.id}`} />On</label>
              <button onClick={() => onTest(r.from_path)} className="text-steel hover:text-accent" title="Test" data-testid={`ur-rule-test-${r.id}`}><FlaskConical size={15} /></button>
              <button onClick={() => onEdit(r)} className="text-steel hover:text-accent" title="Edit" data-testid={`ur-rule-edit-${r.id}`}><Pencil size={15} /></button>
              <button onClick={() => del(r)} className="text-red-600 hover:text-red-700" title="Delete" data-testid={`ur-rule-del-${r.id}`}><Trash2 size={15} /></button>
            </div>))}
      </div>
      {data && data.total > 50 && <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - 50))}>Previous</Button>
        <Button size="sm" variant="outline" disabled={skip + 50 >= data.total} onClick={() => setSkip(skip + 50)}>Next</Button></div>}
    </div>
  );
}
